/**
 * Single source of truth for the JWT.
 *
 * The token lives in `sessionStorage` (not `localStorage`) so it is scoped to the
 * browser tab and dies with the session — this is the behaviour the integration
 * roadmap specifies. `src/services/api.js` reads from here on every request, so
 * the interceptor and the React auth context can never disagree about the token.
 */

const TOKEN_KEY = "swastik_token";
const USER_KEY = "swastik_user";

const safeStorage = () => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    // Safari private mode / blocked storage — degrade to in-memory only.
    return null;
  }
};

let memoryToken = null;
let memoryUser = null;

export function getToken() {
  const store = safeStorage();
  if (!store) return memoryToken;
  try {
    return store.getItem(TOKEN_KEY);
  } catch {
    return memoryToken;
  }
}

export function setToken(token) {
  memoryToken = token ?? null;
  const store = safeStorage();
  if (!store) return;
  try {
    if (token) store.setItem(TOKEN_KEY, token);
    else store.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — the in-memory copy above still works for this tab */
  }
}

export function getStoredUser() {
  const store = safeStorage();
  if (!store) return memoryUser;
  try {
    const raw = store.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : memoryUser;
  } catch {
    return memoryUser;
  }
}

export function setStoredUser(user) {
  memoryUser = user ?? null;
  const store = safeStorage();
  if (!store) return;
  try {
    if (user) store.setItem(USER_KEY, JSON.stringify(user));
    else store.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

export function clearAuth() {
  setToken(null);
  setStoredUser(null);
}

export const AUTH_EVENTS = {
  /** Fired by the API layer when the server rejects the token. */
  unauthorized: "swastik:unauthorized",
  /** Fired after a successful login/register so other tabs/contexts can react. */
  changed: "swastik:auth-changed",
};

export function emit(eventName, detail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
}
